import React from 'react'
import { useAtomValue } from 'jotai'
import { useAtomValueLoadable } from 'src/utils/jotai'
import { useCachedRecoilValue } from 'src/utils/recoil'
import styled from '@emotion/styled'
import { MapExplorerSearchResults } from 'src/views/map-explorer/sidebar/panels/results'
import { MapExplorerSelectedArea } from 'src/views/map-explorer/sidebar/panels/selected-area'
import { MapExplorerSelectedAddress } from 'src/views/map-explorer/sidebar/panels/selected-address'
import { MapExplorerSelectedLocation } from 'src/views/map-explorer/sidebar/panels/selected-location'
import { MapExplorerSelectedShoppingCenter } from 'src/views/map-explorer/sidebar/panels/selected-shopping-center'
import { MapExplorerSelectedBrand } from 'src/views/map-explorer/sidebar/panels/selected-brand'
import { MapExplorerDefaultPanel } from 'src/views/map-explorer/sidebar/panels/default'
import { MapExplorerSelectedParcel } from 'src/views/map-explorer/sidebar/panels/selected-parcel'
import { MapExplorerSelectedOwner } from 'src/views/map-explorer/sidebar/panels/selected-owner'
import { SidebarPanelSkeletonLoader, SidebarResultsSkeletonLoader } from 'src/views/map-explorer/sidebar/panels/loader'
import { FANCY_SCROLLBAR } from 'src/utils/styles'
import { AlphamapLogoThrobber } from 'src/components/alphamap-logo/throbber'
import { useRefreshPlaceId } from 'src/components/sidebar-panels/not-found'
import { useSearchFromUrlParam } from 'src/hooks/use-search-from-url-param'
import { useSaveRecentEntity } from 'src/components/sidebar-panels/recently-viewed/hooks'

import { googlePlace } from 'src/atoms/search'
import * as atoms from 'src/v2/state/map-explorer/sidebar'
import * as searchAtoms from 'src/v2/state/map-explorer/search'
import { DEFAULT_MAP_EXPLORER_SEARCH_OPTIONS } from 'src/views/map-explorer/sidebar/search'

export const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  ${FANCY_SCROLLBAR}
`

const ThrobberWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

export const SidebarPanelThrobber = () => {
  return (
    <ThrobberWrapper>
      <AlphamapLogoThrobber />
    </ThrobberWrapper>
  )
}

export const MapExplorerPanels = () => {
  const searchState = useAtomValueLoadable(atoms.searchResultsState)
  const selectedState = useAtomValueLoadable(atoms.selectedEntityState)
  const loading = useAtomValue(searchAtoms.isSearching)
  const place = useCachedRecoilValue(googlePlace, undefined)

  useRefreshPlaceId(place)
  useSearchFromUrlParam(DEFAULT_MAP_EXPLORER_SEARCH_OPTIONS)
  useSaveRecentEntity()

  // When searching use the results loading component
  if (loading || searchState.state === 'loading' || searchState.state !== 'hasData') {
    return <SidebarResultsSkeletonLoader />
  }

  // When selecting an entity use the skeleton panel loader
  if (selectedState.state === 'loading' || selectedState.state !== 'hasData') {
    return <SidebarPanelSkeletonLoader />
  }

  return (
    <>
      <Main id="mex-sidebar-content">
        {searchState.data === 'EMPTY' && selectedState.data === 'EMPTY' && <MapExplorerDefaultPanel />}
        {searchState.data === 'RESULTS' && selectedState.data === 'EMPTY' && <MapExplorerSearchResults />}
        <React.Suspense fallback={<SidebarPanelSkeletonLoader />}>
          {selectedState.data === 'SELECTED_AREA' && <MapExplorerSelectedArea />}
          {selectedState.data === 'SELECTED_ADDRESS' && <MapExplorerSelectedAddress />}
          {selectedState.data === 'SELECTED_LOCATION' && <MapExplorerSelectedLocation />}
          {selectedState.data === 'SELECTED_SHOPPING_CENTER' && <MapExplorerSelectedShoppingCenter />}
          {selectedState.data === 'SELECTED_BRAND' && <MapExplorerSelectedBrand />}
          {selectedState.data === 'SELECTED_PARCEL' && <MapExplorerSelectedParcel />}
          {selectedState.data === 'SELECTED_OWNER' && <MapExplorerSelectedOwner />}
        </React.Suspense>
      </Main>
    </>
  )
}
